import {
  KeyOutlined,
  LogoutOutlined,
  SafetyOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Form, Input, Modal, Select, Spin, message } from 'antd';
import { createStyles } from 'antd-style';
import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { OpenAPI } from '@/client';
import type { AdminGroupPublic } from '@/client/types.gen';
import { adminRbacFacade, adminUsersFacade } from '@/facades/systemAdmin';
import { buildPreviewCurrentUser, clearPermissionPreview, getPermissionPreviewGroupId, setPermissionPreviewGroupId } from '@/utils/permissionPreview';
import { removeAdminTenant } from '@/utils/adminTenant';
import { getToken, removeToken } from '@/utils/token';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.username || currentUser?.email}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  /**
   * 退出登录，清除 token 并跳转到登录页
   */
  const loginOut = async () => {
    // 清除本地存储的 token
    removeToken();
    removeAdminTenant();
    clearPermissionPreview();
    // 清除 OpenAPI 中的 token
    OpenAPI.TOKEN = async () => getToken() || '';

    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const searchParams = new URLSearchParams({
      redirect: pathname + search,
    });
    /** 此方法会跳转到 redirect 参数所在的位置 */
    const redirect = urlParams.get('redirect');
    // Note: There may be security issues, please note
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');
  const [passwordModalVisible, setPasswordModalVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewSubmitting, setPreviewSubmitting] = useState(false);
  const [groupOptions, setGroupOptions] = useState<AdminGroupPublic[]>([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [form] = Form.useForm<{
    current_password: string;
    new_password: string;
    confirm_password: string;
  }>();
  const [previewForm] = Form.useForm<{ group_id: number | null }>();

  const baseCurrentUser = initialState?.baseCurrentUser || initialState?.currentUser;
  const isSuperUser = Boolean(baseCurrentUser?.is_superuser);
  const previewGroupId = getPermissionPreviewGroupId();

  useEffect(() => {
    if (!previewModalVisible) {
      return;
    }
    previewForm.setFieldsValue({
      group_id: previewGroupId,
    });
  }, [previewGroupId, previewForm, previewModalVisible]);

  const loadGroupOptions = async () => {
    if (groupOptions.length > 0) {
      return groupOptions;
    }
    setGroupLoading(true);
    try {
      const result = await adminRbacFacade.listGroups({ skip: 0, limit: 1000 });
      const options = result.data;
      setGroupOptions(options);
      return options;
    } catch (error: any) {
      message.error(error?.body?.detail || '加载权限组失败');
      return [];
    } finally {
      setGroupLoading(false);
    }
  };

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      flushSync(() => {
        setInitialState((s) => ({ ...s, currentUser: undefined }));
      });
      loginOut();
      return;
    }
    if (key === 'changePassword') {
      form.resetFields();
      setPasswordModalVisible(true);
      return;
    }
    if (key === 'permissionPreview') {
      if (!isSuperUser) {
        message.warning('Only super admins can use permission preview');
        return;
      }
      setPreviewModalVisible(true);
      void loadGroupOptions();
      return;
    }
    if (key === 'clearPermissionPreview') {
      clearPermissionPreview();
      setInitialState((state) => {
        const base = state?.baseCurrentUser || state?.currentUser;
        return base ? { ...state, currentUser: base } : state;
      });
      message.success('已退出临时权限组');
      return;
    }
    history.push(`/account/${key}`);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser || (!currentUser.username && !currentUser.email)) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'center',
            icon: <UserOutlined />,
            label: '个人中心',
          },
          {
            key: 'settings',
            icon: <SettingOutlined />,
            label: '个人设置',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'changePassword',
      icon: <KeyOutlined />,
      label: '修改密码',
    },
    ...(isSuperUser
      ? [
          {
            key: 'permissionPreview',
            icon: <SafetyOutlined />,
            label: previewGroupId ? '临时权限组' : '切换权限组',
          },
          ...(previewGroupId
            ? [
                {
                  key: 'clearPermissionPreview',
                  icon: <LogoutOutlined />,
                  label: '退出临时权限组',
                },
              ]
            : []),
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <>
      <HeaderDropdown
        menu={{
          selectedKeys: [],
          onClick: onMenuClick,
          items: menuItems,
        }}
      >
        {children}
      </HeaderDropdown>
      <Modal
        title="修改密码"
        open={passwordModalVisible}
        onCancel={() => setPasswordModalVisible(false)}
        confirmLoading={submitting}
        onOk={async () => {
          const values = await form.validateFields();
          setSubmitting(true);
          try {
            await adminUsersFacade.updatePasswordMe({
              current_password: values.current_password,
              new_password: values.new_password,
            });
            message.success('密码已更新');
            setPasswordModalVisible(false);
            form.resetFields();
          } catch (error: any) {
            message.error(error?.body?.detail || '修改密码失败');
          } finally {
            setSubmitting(false);
          }
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="current_password"
            label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[{ required: true, message: '请输入新密码' }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || value === getFieldValue('new_password')) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的新密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="临时切换权限组"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        confirmLoading={previewSubmitting}
        okText="应用"
        cancelText="取消"
        onOk={async () => {
          const values = await previewForm.validateFields();
          if (!values.group_id) {
            clearPermissionPreview();
            setInitialState((state) => {
              const base = state?.baseCurrentUser || state?.currentUser;
              return base ? { ...state, currentUser: base } : state;
            });
            setPreviewModalVisible(false);
            return;
          }
          setPreviewSubmitting(true);
          try {
            const options = await loadGroupOptions();
            const matched = options.find((item) => item.id === values.group_id);
            if (!matched) {
              throw new Error('Group not found');
            }
            setPermissionPreviewGroupId(matched.id);
            setInitialState((state) => {
              const base = state?.baseCurrentUser || state?.currentUser;
              if (!base) {
                return state;
              }
              return {
                ...state,
                currentUser: buildPreviewCurrentUser(base, matched),
              };
            });
            message.success(`Switched to ${matched.name}`);
            setPreviewModalVisible(false);
          } catch (error: any) {
            message.error(error?.body?.detail || error?.message || '切换失败');
          } finally {
            setPreviewSubmitting(false);
          }
        }}
        destroyOnClose
      >
        <Form
          form={previewForm}
          layout="vertical"
          initialValues={{ group_id: previewGroupId }}
        >
          <Form.Item name="group_id" label="权限组">
            <Select
              allowClear
              showSearch
              placeholder="选择一个权限组"
              options={groupOptions.map((item) => ({
                label: `${item.name} (${item.code})`,
                value: item.id,
                disabled: !item.is_active,
              }))}
              loading={groupLoading}
              optionFilterProp="label"
              onDropdownVisibleChange={async (open) => {
                if (!open || groupOptions.length > 0) {
                  return;
                }
                await loadGroupOptions();
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
